import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { definirSessao } from '../sessao';
import useDialogoTeclado from '../hooks/useDialogoTeclado';
import ModalEsqueciSenha from '../components/ModalEsqueciSenha';
import Icon from '../components/ui/Icon';

const TELA = { LOGIN: 'login', CRIAR: 'criar', CODIGO: 'codigo' };

function titleCase(str) {
  if (!str) return '';
  return str.replace(/\S+/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase());
}

export default function Login({ onLogin, telaInicial }) {
  const [tela, setTela] = useState(telaInicial === 'criar' ? TELA.CRIAR : TELA.LOGIN);
  const [form, setForm] = useState({ nome: '', email: '', senha: '', nomeFazenda: '', cidade: '', codigoConvite: '' });
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  const [msgTimeout, setMsgTimeout] = useState(false);
  const [aguardandoAprovacao, setAguardandoAprovacao] = useState(false);
  const [modalEsqueci, setModalEsqueci] = useState(false);
  const [videoAberto, setVideoAberto] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const timeoutRef = useRef(null);

  // O vídeo tem caixa própria (largura do player, fundo preto), então não cabe no
  // Modal base sem mudar o visual: o comportamento de teclado vem do hook.
  const videoRef = useDialogoTeclado(videoAberto, () => setVideoAberto(false));

  useEffect(() => { api.healthCheck().catch(() => {}); }, []);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const irPara = (novaTela) => {
    setTela(novaTela);
    setErro('');
    setMsgTimeout(false);
    setLoading(false);
    setMostrarSenha(false);
    setForm({ nome: '', email: '', senha: '', nomeFazenda: '', cidade: '', codigoConvite: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setMsgTimeout(false);
    setLoading(true);

    timeoutRef.current = setTimeout(() => {
      setLoading(false);
      setMsgTimeout(true);
    }, 8000);

    try {
      if (tela === TELA.LOGIN) {
        const res = await api.login(form.email, form.senha);
        clearTimeout(timeoutRef.current);
        localStorage.setItem('token', res.token);
        localStorage.setItem('usuario', titleCase(res.usuario));
        if (res.emailPendente) localStorage.setItem('emailPendente', 'true');
        else localStorage.removeItem('emailPendente');
        // Multi-fazenda: grava a lista de fazendas e a ativa; as chaves planas
        // (role/fazenda/codigoConvite) passam a refletir a fazenda ativa.
        definirSessao({ fazendas: res.fazendas, fazendaAtivaId: res.fazendaAtivaId });
        onLogin();
      } else if (tela === TELA.CRIAR) {
        const res = await api.criarFazenda({ nomeFazenda: form.nomeFazenda, cidade: form.cidade, nome: form.nome, email: form.email, senha: form.senha });
        clearTimeout(timeoutRef.current);
        localStorage.setItem('token', res.token);
        localStorage.setItem('usuario', titleCase(res.usuario));
        localStorage.removeItem('emailPendente');
        definirSessao({ fazendas: res.fazendas, fazendaAtivaId: res.fazendaAtivaId });
        onLogin();
      } else {
        await api.entrarComCodigo({ codigoConvite: form.codigoConvite, nome: form.nome, email: form.email, senha: form.senha });
        clearTimeout(timeoutRef.current);
        setLoading(false);
        setAguardandoAprovacao(true);
      }
    } catch (err) {
      clearTimeout(timeoutRef.current);
      setLoading(false);
      setMsgTimeout(false);
      // O backend responde {"erro": "..."} e o api.js expõe o status em err.status,
      // então a mensagem já vem pronta — só o caso PENDENTE precisa de tradução.
      if (err.status === 403 && err.message === 'PENDENTE') {
        setErro('Sua conta ainda está aguardando aprovação do administrador.');
      } else {
        setErro(err.message || 'Erro ao processar. Tente novamente.');
      }
    }
  };

  if (aguardandoAprovacao) return (
    <div className="login-wrapper">
      <div className="login-card">
        <div className="login-logo login-logo--coluna">
          <span className="marca-icone marca-icone--lg" aria-hidden="true">
            <img src={process.env.PUBLIC_URL + '/icone-sem-fundo.png'} alt="" />
          </span>
          <div className="login-logo-title">AgriGest</div>
        </div>
        <div className="login-aguardando">
          Aguarde o administrador da fazenda aprovar seu acesso.<br />
          Após a aprovação, faça login normalmente.
        </div>
        <button className="btn-primary botao-largo" onClick={() => { setAguardandoAprovacao(false); irPara(TELA.LOGIN); }}>
          Ir para o login
        </button>
      </div>
    </div>
  );

  return (
    <div className="login-wrapper">
      {/* Modal de vídeo tutorial */}
      {videoAberto && (
        <div className="modal-overlay" onClick={() => setVideoAberto(false)} role="presentation">
          <div
            ref={videoRef}
            role="dialog"
            aria-modal="true"
            aria-label="Vídeo: como usar o AgriGest"
            tabIndex={-1}
            onClick={e => e.stopPropagation()}
            className="login-video"
          >
            <button type="button" onClick={() => setVideoAberto(false)} aria-label="Fechar vídeo" className="login-video__fechar">
              <Icon nome="fechar" tamanho={16} />
            </button>
            <video controls autoPlay playsInline>
              <source src={process.env.PUBLIC_URL + '/tutorial-agrigest.mp4'} type="video/mp4" />
              Seu navegador não suporta vídeo HTML5.
            </video>
          </div>
        </div>
      )}

      <div className="login-card">
        <div className="login-carimbo">
          2 meses grátis<br />
          <span className="login-carimbo__sub">após, a partir de R$ 6,99/mês</span><br />
          <span className="login-carimbo__nota">Sem precisar cancelar, pague somente se continuar usando</span>
        </div>

        <div className="login-logo">
          <span className="marca-icone marca-icone--md" aria-hidden="true">
            <img src={process.env.PUBLIC_URL + '/icone-sem-fundo.png'} alt="" />
          </span>
          <div>
            <div className="login-logo-title">AgriGest</div>
            <div className="login-logo-sub">Gestão Agrícola</div>
          </div>
        </div>

        <button type="button" onClick={() => setVideoAberto(true)} className="login-video-btn">
          <Icon nome="play" tamanho={16} />
          Clique aqui e assista o que o sistema oferece e como utilizar
        </button>

        {erro && <div className="msg msg-error">{erro}</div>}
        {msgTimeout && <div className="msg msg-error">Estamos iniciando a aplicação. Aguarde 15 segundos e tente novamente.</div>}

        {tela === TELA.LOGIN && (
          <>
            <form onSubmit={handleSubmit} autoComplete="on">
              <div className="login-field"><label>Email</label><input type="email" name="email" autoComplete="username" value={form.email} onChange={set('email')} required placeholder="seuemail@exemplo.com" autoFocus /></div>
              <div className="login-field--aperto"><label>Senha</label>
                <div className="input-senha">
                  <input type={mostrarSenha ? 'text' : 'password'} name="password" autoComplete="current-password" value={form.senha} onChange={set('senha')} required />
                  <button type="button" className="input-senha__toggle" onClick={() => setMostrarSenha((v) => !v)} aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'} aria-pressed={mostrarSenha}>
                    <Icon nome={mostrarSenha ? 'olho-fechado' : 'olho'} tamanho={18} />
                  </button>
                </div>
              </div>
              <div className="login-esqueci">
                <button type="button" onClick={() => setModalEsqueci(true)}>
                  Esqueci minha senha
                </button>
              </div>
              <button type="submit" className="btn-primary botao-largo" disabled={loading}>
                {loading ? 'Aguarde...' : 'Entrar'}
              </button>
            </form>
            <div className="login-alt">
              <p>Primeira vez no AgriGest?</p>
              <div className="login-alt__acoes">
                <button type="button" className="btn-secondary botao-largo" onClick={() => irPara(TELA.CRIAR)}>
                  <Icon nome="safra" tamanho={16} />
                  Cadastre-se
                </button>
                <button type="button" className="btn-secondary botao-largo" onClick={() => irPara(TELA.CODIGO)}>
                  <Icon nome="chave" tamanho={16} />
                  Entrar em fazenda existente
                </button>
              </div>
              <a href="https://wa.me/5554999487383?text=Ol%C3%A1!%20Gostaria%20de%20falar%20sobre%20o%20AgriGest" target="_blank" rel="noopener noreferrer" className="login-ajuda">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Precisa de ajuda? Fale conosco
              </a>
            </div>
          </>
        )}

        {tela === TELA.CRIAR && (
          <>
            <div className="login-titulo-bloco">
              <div className="login-titulo">
                <Icon nome="safra" tamanho={18} /> Cadastrar minha fazenda
              </div>
              <p>Crie sua conta e sua fazenda de uma vez</p>
            </div>
            <form onSubmit={handleSubmit} autoComplete="on">
              <div className="login-field"><label>Nome da fazenda</label><input name="organization" autoComplete="organization" value={form.nomeFazenda} onChange={set('nomeFazenda')} required autoFocus /></div>
              <div className="login-field"><label>Cidade</label><input name="city" autoComplete="address-level2" value={form.cidade} onChange={set('cidade')} /></div>
              <div className="login-field"><label>Seu nome (usuário)</label><input name="name" autoComplete="name" value={form.nome} onChange={set('nome')} required /></div>
              <div className="login-field"><label>Email</label><input type="email" name="email" autoComplete="email" value={form.email} onChange={set('email')} required placeholder="seuemail@exemplo.com" /></div>
              <div className="login-field--ultimo"><label>Senha (mín. 6 caracteres)</label>
                <div className="input-senha">
                  <input type={mostrarSenha ? 'text' : 'password'} name="password" autoComplete="new-password" value={form.senha} onChange={set('senha')} required minLength={6} />
                  <button type="button" className="input-senha__toggle" onClick={() => setMostrarSenha((v) => !v)} aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'} aria-pressed={mostrarSenha}>
                    <Icon nome={mostrarSenha ? 'olho-fechado' : 'olho'} tamanho={18} />
                  </button>
                </div>
              </div>
              <button type="submit" className="btn-primary botao-largo" disabled={loading}>
                {loading ? 'Aguarde...' : 'Criar fazenda'}
              </button>
            </form>
            <button type="button" className="login-voltar" onClick={() => irPara(TELA.LOGIN)}>Voltar ao login</button>
          </>
        )}

        {tela === TELA.CODIGO && (
          <>
            <div className="login-titulo-bloco">
              <div className="login-titulo">
                <Icon nome="chave" tamanho={18} /> Entrar em fazenda existente
              </div>
              <p>
                Para quem vai colaborar na gestão de uma fazenda já cadastrada — como
                parceiro, familiar ou funcionário. Peça o código de acesso ao administrador
                da fazenda.
              </p>
            </div>
            <form onSubmit={handleSubmit} autoComplete="on">
              <div className="login-field">
                <label>Código de convite</label>
                <input name="invite-code" autoComplete="off" className="login-codigo" value={form.codigoConvite} onChange={e => {
                  let v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                  if (v.length === 3 && !v.includes('-')) v = v + '-';
                  if (v.length > 3 && v[3] !== '-') v = v.slice(0, 3) + '-' + v.slice(3);
                  if (v.length > 10) v = v.slice(0, 10);
                  setForm(f => ({ ...f, codigoConvite: v }));
                }} placeholder="AGR-XXXXXX" required autoFocus />
              </div>
              <div className="login-field"><label>Seu nome (usuário)</label><input name="name" autoComplete="name" value={form.nome} onChange={set('nome')} required /></div>
              <div className="login-field"><label>Email</label><input type="email" name="email" autoComplete="email" value={form.email} onChange={set('email')} required placeholder="seuemail@exemplo.com" /></div>
              <div className="login-field--ultimo"><label>Senha (mín. 6 caracteres)</label>
                <div className="input-senha">
                  <input type={mostrarSenha ? 'text' : 'password'} name="password" autoComplete="new-password" value={form.senha} onChange={set('senha')} required minLength={6} />
                  <button type="button" className="input-senha__toggle" onClick={() => setMostrarSenha((v) => !v)} aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'} aria-pressed={mostrarSenha}>
                    <Icon nome={mostrarSenha ? 'olho-fechado' : 'olho'} tamanho={18} />
                  </button>
                </div>
              </div>
              <button type="submit" className="btn-primary botao-largo" disabled={loading}>
                {loading ? 'Aguarde...' : 'Solicitar acesso'}
              </button>
            </form>
            <button type="button" className="login-voltar" onClick={() => irPara(TELA.LOGIN)}>Voltar ao login</button>
          </>
        )}
      </div>

      {modalEsqueci && (
        <ModalEsqueciSenha
          emailInicial={form.email}
          onClose={() => setModalEsqueci(false)}
        />
      )}
    </div>
  );
}
